import React from 'react';

import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';

import Button from '@/lib/components/Button';
import FormField from '@/lib/components/FormField';
import InfoBlock from '@/lib/components/InfoBlock';
import { useFormSave } from '@/lib/hooks/useFormSave';

import styles from '@/lib/components/PspComposer/PspComposer.module.scss';
import chileanFormStyles from './ChileanForm.module.scss';

/**
 * This component is one of the multiple available payment method in this project.
 * It represents a form including the Chilean RUT number and the bank account number,
 * only used to collect refund request data for now.
 */
const ChileanForm = React.forwardRef(
    (
        {
            pspId,
            token,
            paymentRequestId,
            lyriaUrl,
            hidePayButton = false,
            labelPayButton = null,  // Fallback on 'pay' or 'ask refund' label.
            onPaymentResult,
        },
        ref,
    ) => {
        const { t } = useTranslation();

        const formRef = React.useRef(null);
        const [formData, setFormData] = React.useState({
            firstName: '',
            lastName: '',
            rutNumber: '',
            bankName: '',
            bankAccountType: '',
            bankAccountNumber: '',
            email: '',
        });
        const [formFieldBlurState, setFormFieldBlurState] = React.useState({
            firstName: false,
            lastName: false,
            rutNumber: false,
            bankName: false,
            bankAccountType: false,
            bankAccountNumber: false,
            email: false,
        });
        const [errors, setErrors] = React.useState({});

        const handleSuccess = React.useCallback(
            (response) => {
                onPaymentResult({ success: true, data: response });
            },
            [onPaymentResult],
        );

        const handleError = React.useCallback(
            (response) => {
                if (response?.data) {
                    const fieldErrors = {};
                    Object
                        .keys(response.data)
                        .forEach((key) => {
                            fieldErrors[key] = response.data[key] && response.data[key].map((error) => t([
                                `chilean_form_${key}_error_${error.code}`,
                                `error_${error.code}`,
                                'error_form',
                            ]));
                        });
                    setErrors(fieldErrors);
                }
                onPaymentResult({
                    success: false,
                    data: response,
                });
            },
            [onPaymentResult, t],
        );
        const url = lyriaUrl + '/chilean-form/save';
        const saveForm = useFormSave(url, token, paymentRequestId, pspId, handleSuccess, handleError);

        const submitForm = React.useCallback(
            (event) => {
                event.preventDefault();
                event.stopPropagation();
                const data = {
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    rut_number: formData.rutNumber,
                    bank_name: formData.bankName,
                    bank_account_type: formData.bankAccountType,
                    bank_account_number: formData.bankAccountNumber,
                    email: formData.email,
                };
                saveForm(data);
            },
            [formData, saveForm],
        );

        const formatAndValidateRutNumber = React.useCallback(
            (value) => {
                let newValue = value.toUpperCase().replace(/\s+/g, '');
                const isNotValid = (
                    (newValue?.length >= 9 || formFieldBlurState?.rutNumber)
                    && !newValue.match(/^(\d{1,2}\.)?\d{3}\.\d{3}-[\dkK]$/g)
                );

                setErrors((prevState) => ({
                    ...prevState,
                    rut_number: isNotValid ? [t('chilean_form_rut_number_error_invalid')] : null,
                }));

                return newValue;
            },
            [t, formFieldBlurState?.rutNumber],
        );

        const onChangeFormField = React.useCallback(
            (fieldName) => (event, newValue) => {
                setFormData((prevState) => ({
                    ...prevState,
                    [fieldName]: newValue,
                }));

            },
            [],
        );

        const onBlurFormField = React.useCallback(
            (fieldName) => (_event) => {
                setFormFieldBlurState((prevState) => ({
                    ...prevState,
                    [fieldName]: true,
                }));
            },
            [],
        );

        React.useImperativeHandle(
            ref,
            () => ({
                pay() {
                    return formRef.current.click();
                },
                canClickOnPay: Object.values(formData).every((value) => !!value),
            }),
            [formData],
        );

        return (
            <div id="wz-lyriapay__chilean-form" className={styles.common_psp}>
                <form noValidate onSubmit={submitForm}>
                    <div className={styles.common_psp_form}>
                        <div className={chileanFormStyles.tips}>
                            <InfoBlock
                                title={t('chilean_form_tips_title')}
                                message={(
                                    <>
                                        <p>{t('chilean_form_tips_bank_account')}</p>
                                        <p>{t('chilean_form_tips_account_holder')}</p>
                                        <p>{t('chilean_form_tips_no_joint_account')}</p>
                                        <p>{t('chilean_form_tips_no_punctuation')}</p>
                                    </>
                                )}
                            />
                        </div>
                        <div className={styles.common_field_group}>
                            <FormField
                                name="first_name"
                                value={formData?.firstName}
                                onChange={onChangeFormField('firstName')}
                                label={t('chilean_form_first_name')}
                                errors={errors.first_name}
                                required
                            />
                            <FormField
                                name="last_name"
                                value={formData?.lastName}
                                onChange={onChangeFormField('lastName')}
                                label={t('chilean_form_last_name')}
                                errors={errors.last_name}
                                required
                            />
                        </div>
                        <FormField
                            name="rut_number"
                            value={formData?.rutNumber}
                            formatAndValidate={formatAndValidateRutNumber}
                            onChange={onChangeFormField('rutNumber')}
                            onBlur={onBlurFormField('rutNumber')}
                            label={t('chilean_form_rut_number')}
                            errors={errors.rut_number}
                            required
                        />
                        <FormField
                            name="bank_name"
                            value={formData?.bankName}
                            onChange={onChangeFormField('bankName')}
                            label={t('chilean_form_bank_name')}
                            errors={errors.bank_name}
                            required
                        />
                        <FormField
                            name="bank_account_type"
                            value={formData?.bankAccountType}
                            onChange={onChangeFormField('bankAccountType')}
                            label={t('chilean_form_bank_account_type')}
                            errors={errors.bank_account_type}
                            required
                            type="select"
                            options={[
                                { value: '', label: '' },
                                { value: 'Ahorros', label: 'Ahorros' },
                                { value: 'Corriente', label: 'Corriente' },
                                { value: 'Vista', label: 'Vista' },
                            ]}
                        />
                        <FormField
                            name="bank_account_number"
                            value={formData?.bankAccountNumber}
                            onChange={onChangeFormField('bankAccountNumber')}
                            label={t('chilean_form_bank_account_number')}
                            errors={errors.bank_account_number}
                            required
                        />
                        <FormField
                            name="email"
                            value={formData?.email}
                            onChange={onChangeFormField('email')}
                            label={t('chilean_form_email')}
                            errors={errors.email}
                            required
                        />
                    </div>
                    {!hidePayButton && (
                        <div className={styles.common_psp_submit}>
                            <Button
                                disabled={Object.values(formData).some((value) => !value)}
                                label={labelPayButton || t('payButton')}
                                ref={formRef}
                                type='submit'
                            />
                        </div>
                    )}
                </form>
            </div>
        );
    },
);

ChileanForm.propTypes = {
    /**
     * The is of the PSP returned by the backend in order to be able to pass it when we submit the form data.
     */
    pspId: PropTypes.number.isRequired,
    /**
     * The token to use in order to be able to call the backend when we submit the form data.
     */
    token: PropTypes.string,
    /**
     * The paymentRequestID to use in order to be able to call the backend when we submit the data in the paymentPage.
     */
    paymentRequestId: PropTypes.string,
    /**
     * The backend url to use to pass the form data.
     */
    lyriaUrl: PropTypes.string.isRequired,
    /**
     * Whether the submit button must be displayed or not.
     */
    hidePayButton: PropTypes.bool,
    /**
     * The label of the 'pay' button to display. hidePayButton props must be false of course.
     */
    labelPayButton: PropTypes.string,
    /**
     * The callback function to trigger when the backend return the result of the form data submit.
     */
    onPaymentResult: PropTypes.func.isRequired,
};

ChileanForm.displayName = 'ChileanForm';

export default ChileanForm;
